import { HangoutState } from '../state/HangoutState.js';
import { storageManager } from '../../../core/storage/index.js';
import { getTranslation } from './modals/settings-dom-utils.js';

export const syncFloatingButtonIcon = () => {
    const mainButton = document.getElementById('mic-send-button');
    const floatingButton = document.getElementById('mic-send-button-floating');
    const isMobile = window.innerWidth <= 1024;

    if (!floatingButton) return;
    if (isMobile) return;
    if (!mainButton) return;

    const mainIcon = mainButton.querySelector('i');
    const floatingIcon = floatingButton.querySelector('i');
    if (!mainIcon || !floatingIcon) return;

    floatingIcon.className = mainIcon.className;
    floatingButton.setAttribute('aria-label', mainButton.getAttribute('aria-label') || 'Send message');

    floatingButton.classList.toggle('recording', mainButton.classList.contains('recording'));
};

export const updateMessagePlaceholder = async () => {
    const chatInput = document.getElementById('chat-input');
    if (!chatInput) return;

    const sanitizeName = (value) => {
        if (!value) return null;
        const trimmed = String(value).trim();
        if (!trimmed || trimmed.toLowerCase() === 'character' || trimmed.toLowerCase() === 'unknown') return null;
        return trimmed;
    };

    let characterName = null;
    const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
    if (Array.isArray(hangoutConfig?.allCharacters) && hangoutConfig.allCharacters.length > 0) {
        const randomIndex = Math.floor(Math.random() * hangoutConfig.allCharacters.length);
        characterName = sanitizeName(hangoutConfig.allCharacters[randomIndex]?.name);
    }

    const placeholder = characterName
        ? await getTranslation('chat_input_placeholder_with_name', { name: characterName })
        : await getTranslation('chat_input_placeholder');

    chatInput.setAttribute('placeholder', placeholder);
};

if (typeof window !== 'undefined') {
    (/** @type {any} */ (window)).updateMessagePlaceholder = updateMessagePlaceholder;
    if (!(/** @type {any} */ (window))._chatInputLangListenerAdded) {
        (/** @type {any} */ (window))._chatInputLangListenerAdded = true;
        document.addEventListener('languageSwitched', () => {
            updateMessagePlaceholder();
        });
    }
}

export function toggleSendMicButton() {
    const chatInput = /** @type {HTMLInputElement} */ (document.getElementById('chat-input'));
    const sendButton = document.getElementById('mic-send-button');
    const floatingButton = document.getElementById('mic-send-button-floating');
    const isMobile = window.innerWidth <= 1024;

    if (!chatInput) return;

    const hasText = chatInput.value.trim().length > 0;

    if (sendButton) {
        const iconElement = sendButton.querySelector('i');
        if (iconElement) {
            if (hasText) {
                iconElement.className = 'fas fa-angle-double-right';
                sendButton.setAttribute('aria-label', 'Send message');
            } else {
                iconElement.className = 'fas fa-microphone';
                sendButton.setAttribute('aria-label', 'Voice input');
            }
        }
    }

    if (isMobile && floatingButton) {
        const floatingIcon = floatingButton.querySelector('i');
        if (floatingIcon) {
            if (hasText) {
                floatingIcon.className = 'fas fa-angle-double-right';
                floatingButton.setAttribute('aria-label', 'Send message');
            } else {
                floatingIcon.className = 'fas fa-microphone';
                floatingButton.setAttribute('aria-label', 'Voice input');
            }
        }
    } else {
        syncFloatingButtonIcon();
    }
}

export async function createChatInputContainer(initializeChatInputListeners) {
    const chatInputContainer = /** @type {HTMLElement} */ (document.querySelector('.chat-input-container'));
    if (!chatInputContainer) return;

    if (initializeChatInputListeners) {
        await initializeChatInputListeners();
    }

    toggleSendMicButton();
    const chatInput = document.getElementById('chat-input');
    if (chatInput && !chatInput.dataset.micToggleBound) {
        chatInput.dataset.micToggleBound = 'true';
        chatInput.addEventListener('input', toggleSendMicButton);
    }

    const mobileVoiceBtn = document.getElementById('mobile-voice-play-btn');
    if (mobileVoiceBtn) {
        mobileVoiceBtn.addEventListener('click', async (e) => {
            e.stopPropagation();

            const { getVoice } = await import('./voice-utils.js');
            const { findMessageIdByText } = await import('./message-key-utils.js');
            const Voice = await getVoice();

            const resumeAudioEntry = async (entry) => {
                if (!entry) return false;

                if (entry.audio) {
                    if (entry.audio.paused) {
                        await entry.audio.play();
                    } else {
                        entry.audio.pause();
                    }
                    return true;
                }

                if (entry.audioPlayer) {
                    const player = entry.audioPlayer;
                    if (player.isPlaying) {
                        player.pause();
                        return true;
                    }

                    if (!player.audioBuffer && player.preparePromise) {
                        try {
                            await player.preparePromise;
                        } catch (error) {
                            console.warn('Mobile button: audio preparation failed, regenerating:', error);
                        }
                    }

                    if (player.audioBuffer && player.chunkTimings) {
                        try {
                            await player.playWithSeparation(
                                player.audioBuffer,
                                player.chunkTimings,
                                player.separationMs || 500
                            );
                            return true;
                        } catch (err) {
                            console.warn('Mobile button: Replay failed, regenerating:', err);
                        }
                    }
                }

                return false;
            };

            const characterId = mobileVoiceBtn.dataset.characterId;
            const messageText = mobileVoiceBtn.dataset.messageText;
            const voiceKey = mobileVoiceBtn.dataset.voiceKey;

            if (voiceKey) {
                const cachedEntry = Voice.getAudioEntryByKey(voiceKey);
                if (await resumeAudioEntry(cachedEntry)) {
                    return;
                }
            }

            if (characterId && messageText) {
                const voiceSettings = Voice.getVoiceSettingsSnapshot(String(characterId));
                const currentAudioEntry = Voice.getCurrentAudioForCharacter(characterId, messageText, voiceSettings);

                if (await resumeAudioEntry(currentAudioEntry)) {
                    return;
                }

                const messageId = await findMessageIdByText(characterId, messageText);
                await Voice.playVoice(characterId, messageText, mobileVoiceBtn, messageId);
            } else {
                const messages = HangoutState.getInstance().getMessages();
                const lastMsg = messages.slice().reverse().find(m => m.role === 'assistant');
                if (lastMsg) {
                    mobileVoiceBtn.dataset.characterId = lastMsg.characterId;
                    mobileVoiceBtn.dataset.messageText = lastMsg.content;
                    const messageId = lastMsg.path || await findMessageIdByText(lastMsg.characterId, lastMsg.content);
                    await Voice.playVoice(lastMsg.characterId, lastMsg.content, mobileVoiceBtn, messageId);
                }
            }
        });
    }
}

const handleOpenSettingsModal = async () => {
    const { openSettingsModal } = await import('./modals/settings-modal.js');
    await openSettingsModal();
};

const settingsBtn = document.getElementById('settings-btn');
if (settingsBtn) {
    settingsBtn.removeEventListener('click', handleOpenSettingsModal);
    settingsBtn.addEventListener('click', handleOpenSettingsModal);
}

const hangoutSettingsButton = document.getElementById('hangout-settings-button');
if (hangoutSettingsButton) {
    const handleHangoutSettingsClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleOpenSettingsModal();
    };
    const handleHangoutSettingsKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.stopPropagation();
        handleOpenSettingsModal();
    };
    hangoutSettingsButton.addEventListener('click', handleHangoutSettingsClick);
    hangoutSettingsButton.addEventListener('keydown', handleHangoutSettingsKeyDown);
}
